import type {
  FieldHtmlNameType,
  HekateDisplayField,
  HekateDisplayForm,
  TranslationLanguage,
} from '@onshape/www-inertia';
import SSOUtil from './util/SSOUtil';
import ErrorUtil from './util/ErrorUtil';

/** Cookies that we set */
type ValidCookieName =
  | 'x-www-plan-group' // crossSession Free, Pro, EDU, etc...
  | 'x-www-is-internal-developer' // from crossSession, true if the user visits heartBeat from a dev server
  | 'x-www-have-seen-on'; // from crossSession, the timestamp in milliseconds when the user has had an "on" cookie,
// meaning they have logged in to the product at some point

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class ExternalUtil {
  public static sso = new SSOUtil();

  /** Supported language */
  private static readonly languages: TranslationLanguage[] = [
    'DE',
    'EN',
    'FR',
    'IT',
    'JA',
    'ES',
    'NL',
  ];

  /** Mapping from pathname to the language of that page */
  private static readonly pageToLanguageMap: { [key: string]: TranslationLanguage } = {
    '/de/lp/nl-professional-trial': 'NL',
    '/de/lp/nl-3d-cad-free-trial-ad': 'NL',
  };

  /** Get the origin (aka domain name) of this instance */
  public static getSafeOrigin(): string {
    if (!window.top) {
      console.error('window.top is null');
      return 'unknown.domain';
    }
    const isSelfOriginAvailable = typeof self === 'object' && typeof self.origin === 'string';
    return isSelfOriginAvailable ? self.origin : window.top.location.toString();
  }

  /** True if this is running on a production domain */
  public static isProduction(): boolean {
    return ExternalUtil.getSafeOrigin() === 'https://www.onshape.com';
  }

  /** True if running on stage */
  public static isStage(): boolean {
    return ExternalUtil.getSafeOrigin() === 'https://www-stage.dev.onshape.com';
  }

  /** Find the display field with the given name in the form or throw */
  public static findDisplayFieldOrThrow({
    formDefinition,
    fieldName,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    fieldName: FieldHtmlNameType;
  }): HekateDisplayField {
    return ExternalUtil.noRefFindDisplayFieldOrThrow({
      formDefinition: formDefinition.value,
      fieldName,
    });
  }

  /** Find the display field value as a references */
  public static findDisplayFieldValOrThrow({
    formDefinition,
    fieldName,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    fieldName: FieldHtmlNameType;
  }): Ref<string | number | boolean> {
    const field = ExternalUtil.noRefFindDisplayFieldOrThrow({
      formDefinition: formDefinition.value,
      fieldName,
    });

    return toRefs(field).val;
  }

  /** Non `Ref` version of find the display field with the given name in the form or throw */
  public static noRefFindDisplayFieldOrThrow({
    formDefinition,
    fieldName,
  }: {
    formDefinition: HekateDisplayForm;
    fieldName: FieldHtmlNameType;
  }): HekateDisplayField {
    const found = formDefinition.displayFields.find(
      (field: HekateDisplayField) => field.htmlName === fieldName
    );
    if (!found) {
      throw ErrorUtil.internalError({ msg: `Could not find field with name ${fieldName}` });
    }
    return found;
  }

  /** Find the display field with the given name in the form or null */
  public static findDisplayFieldOrNull({
    formDefinition,
    fieldName,
  }: {
    formDefinition: Ref<HekateDisplayForm | null>;
    fieldName: FieldHtmlNameType;
  }): HekateDisplayField | null {
    if (formDefinition.value === null) {
      return null;
    }
    return (
      formDefinition.value.displayFields.find(
        (field: HekateDisplayField) => field.htmlName === fieldName
      ) || null
    );
  }

  /** Smoothly scroll to the top of form with the given form id. */
  public static scrollToTopOfForm({ formId }: { formId: string }): void {
    try {
      const formBox =
        document.querySelector('#uss-form-container') ||
        document.querySelector(`.formfort-form-container[form-id="${formId}"]`) ||
        document.querySelector(`[form-id="${formId}"]`);

      const scrollTo = formBox!.parentElement!.getBoundingClientRect().y + window.scrollY - 180;
      window.scrollTo({ left: window.scrollX, top: scrollTo, behavior: 'smooth' });
    } catch (e) {
      console.error(e);
    }
  }

  /** Get the site language */
  public static getSiteLang(): TranslationLanguage {
    let fieldValue = 'EN'; // default to en

    const { pathname } = document.location;
    if (ExternalUtil.pageToLanguageMap[pathname]) {
      return ExternalUtil.pageToLanguageMap[pathname];
    }

    const pathBits = pathname.split('/');
    pathBits.shift();
    const firstBit = pathBits.shift();
    if (firstBit) {
      const targetLanguage = firstBit.toLocaleUpperCase() as TranslationLanguage;
      fieldValue =
        ExternalUtil.languages.includes(targetLanguage) ? firstBit.toLocaleUpperCase() : 'EN';
    }

    return fieldValue as TranslationLanguage;
  }

  /** Get the named cookie value as a string */
  public static getCookieStringByName(cookieName: ValidCookieName): string {
    try {
      const found = document.cookie
        .split(';')
        .map((k) => `${k}`.trim())
        .filter((k) => k.startsWith(cookieName))
        .map((k) => {
          const bits = k.split('=');
          bits.shift();
          return bits.join('=');
        });

      return found.length > 0 ? (found[found.length - 1] ?? '') : '';
    } catch {
      console.log('could not parse cookie');
      return '';
    }
  }

  /**
   * Parses the url encoded value of the cookie named `cookieName` and return a new copy
   * of defaults with the values from that cookie replacing any of the default values that
   * are present in the cookie.
   */
  public static parsePreferenceCookie<T extends { [key: string]: string | boolean }>({
    cookieName,
    defaults,
  }: {
    cookieName: string;
    defaults: T;
  }): T {
    const cookieString = new RegExp(`${cookieName}=[^;]+`).exec(document.cookie);
    if (!cookieString) {
      return defaults;
    }

    const safeCookieString = cookieString ? cookieString.toString().replace(/^[^=]+./, '') : '';
    const cookieValue = decodeURIComponent(safeCookieString).replace('+', '%2B');
    if (!cookieValue) {
      return defaults;
    }

    // const queryParams = decodeURIComponent(cookieValue ? cookieValue.toString().replace(/^[^=]+./, '') : '');
    const uri = new URL(`https://example.com/?${cookieValue}`);
    const prams = uri.searchParams;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = JSON.parse(JSON.stringify(defaults));
    // because we are iterating over the clone, it will be the same type at runtime as T
    Object.entries(defaults).forEach(([kk, vv]) => {
      if (typeof vv === 'boolean') {
        result[kk] = prams.has(kk) ? `${prams.get(kk)}` === 'true' : vv;
      } else if (prams.has(kk)) {
        result[kk] = prams.get(kk);
      }
    });

    return result as T;
  }

  /**
   * Populate the form with default values.
   *
   * Look in `defaults` for keys with name `fieldNames`
   *
   * Throws if you look for a default value that is undefined.
   */
  public static populateDefaultFields<T extends { [key: string]: string | boolean }>({
    formDefinition,
    fieldNames,
    defaults,
  }: {
    formDefinition: Ref<HekateDisplayForm>;
    fieldNames: FieldHtmlNameType[];
    defaults: T;
  }): void {
    fieldNames.forEach((fieldName) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const defaultVal = (defaults as any)[fieldName];
      if (typeof defaultVal === 'undefined') {
        throw ErrorUtil.internalError({
          msg: `HekateError unknown default for fieldName ${fieldName}`,
        });
      }

      const field = ExternalUtil.findDisplayFieldOrThrow({
        formDefinition,
        fieldName,
      });

      if (field.val === defaultVal) {
        return;
      }

      field.val = defaultVal;
    });
  }

  /**
   * Reset the recaptcha iff there the library is loaded and the document is ready
   * See also `RecaptchaCoordinator.ts`
   */

  public static async safeResetRecaptcha(): Promise<boolean> {
    let maxTries = 10 * 4;
    return new Promise((res) => {
      const interval = setInterval(() => {
        maxTries -= 1;
        if (maxTries < 1) {
          clearInterval(interval);
          res(false);
        }

        // noop if no grecaptcha
        if (!window.grecaptcha) {
          return;
        }

        // noop inf no recaptcha element
        if (
          !document.getElementById('hk-recaptcha-element-v2') &&
          !document.getElementById('hk-recaptcha-element-v3')
        ) {
          return;
        }

        // only reset if there is element where the recaptcha lives See `RecaptchaCoordinator.ts`
        window.grecaptcha.reset();
        clearInterval(interval);
        console.log('Resetting recaptcha');
        res(true);
      }, 250);
    });
  }

  /** Get page version token typically for clearing cache */
  public static getPageVersion(): string {
    const elm = document.querySelector("[name='page-version']") as HTMLElement | undefined;
    if (!elm || !elm.getAttribute('value')) {
      return Date.now().toString().substring(0, 8);
    }

    return elm.getAttribute('value') as string;
  }

  /**
   * Wait until promise resolves true or timeoutMilliseconds.
   * timeoutMilliseconds must be a positive whole number
   */
  public static async fluidWait({
    cb,
    timeoutMilliseconds,
  }: {
    cb: () => Promise<boolean>;
    timeoutMilliseconds: number;
  }) {
    let maxTries = 50;
    const stepSize = Math.ceil(timeoutMilliseconds / maxTries);
    while (maxTries > 0 && !(await cb())) {
      await ExternalUtil.sleep(stepSize);
      maxTries -= 1;
    }
  }

  /**
   * Instead of making this public use fluidWait
   */
  private static sleep(timeoutMilliseconds: number): Promise<void> {
    return new Promise((res) => {
      setTimeout(() => res(), timeoutMilliseconds);
    });
  }

  /** Create a script tag and append to the bottom of the page */
  public static createScriptTag({
    src,
    isAsync,
    className,
  }: {
    src: string;
    isAsync: boolean;
    className: string;
  }) {
    const scriptTag = document.createElement('script');
    scriptTag.async = isAsync;
    scriptTag.src = src;
    scriptTag.className = className;
    document.body.appendChild(scriptTag);
  }

  /** parsing string into HTML Node */
  public static stringToHTML(text: string): Node {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    const container = document.createElement('div');
    const scriptNodes = Array.from(doc.getElementsByTagName('script')).map((node) => {
      const scriptTag = document.createElement('script');
      scriptTag.type = 'text/javascript';
      if (node.src !== '') {
        scriptTag.src = node.src;
      } else {
        scriptTag.innerHTML = node.innerHTML;
      }
      return scriptTag;
    });
    const styleNodes = Array.from(doc.getElementsByTagName('style'));
    Array.from(doc.body.children).forEach((node) => {
      const tagName = node.tagName.toLowerCase();
      if (tagName !== 'script' && tagName !== 'style') {
        container.appendChild(node);
      }
    });
    [...scriptNodes, ...styleNodes].forEach((node) => {
      container.appendChild(node);
    });
    return container;
  }

  /** remove any html and throw if there is scripts tags */
  public static sanitizeHtml(input: string) {
    if (input.includes('<script ')) {
      throw new Error('sanitizeHtml - Input contains <script tag');
    }
    // Create a temporary DOM element to use its innerHTML property
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = input;

    // Remove all script elements
    const scripts = tempDiv.getElementsByTagName('script');
    if (scripts.length > 0) throw new Error('sanitizeHtml - Input still contains <script tag');

    // Return the text content of the temporary element, which strips out all HTML tags
    const html = tempDiv.textContent || tempDiv.innerText || '';
    return html.replace('<', '&lt;').replace('>', '&gt;');
  }
}
